import { ChartTooltip } from "@/components/Charts/ChartTooltip";
import { dataColors } from "@/components/Charts/theme";
import { formatDate } from "@/components/FormattedDate/FormattedDate";
import { ANALYTICS_LABELED_Y_AXIS_WIDTH } from "@/modules/analytics/constants";
import { useTranslation } from "@/translations";
import {
  CartesianGrid,
  Label,
  Line,
  LineChart as RechartsLineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "@/ui-adapters/Recharts";

interface DataPoint {
  x: Date | string;
  y: number;
  isPending?: boolean;
}

interface CustomDotProps {
  cx?: number;
  cy?: number;
  payload?: Record<string, unknown>;
  dataKey?: string;
  fill?: string;
}

function CustomDot({ cx, cy, payload, dataKey, fill }: CustomDotProps) {
  if (cx === undefined || cy === undefined || !payload || !dataKey) {
    return null;
  }

  // Solid circle for non-pending data
  return <circle cx={cx} cy={cy} r={5} fill={fill} fillOpacity={payload.isPending ? 0.5 : 1} />;
}

interface LineData {
  label: string;
  dataPoints: DataPoint[];
  color: string;
}

interface Props {
  data: LineData[];
  hoverLabel: (
    data: DataPoint & { relatedPoints: { label?: string; dataPoint: DataPoint; color: string }[] },
  ) => React.ReactNode;
  formatYTick?: (y: number) => string;
  xLabel?: string;
  yLabel?: string;
  maxY?: number;
  xAxisType?: "date";
}

/** Renders analytics line series from labeled collections of X/Y data points. */
export function LineChart({ xLabel, yLabel, hoverLabel, data, maxY, xAxisType, formatYTick }: Props): React.ReactNode {
  const { i18n } = useTranslation();

  return (
    <ResponsiveContainer minHeight={200} className="flex-1">
      <RechartsLineChart
        className="no-top-bottom-lines"
        data={Array.from(
          data
            .flatMap(({ label, dataPoints }) => dataPoints.map(({ x, y, isPending }) => ({ x, [label]: y, isPending })))
            .reduce((map, { x, ...rest }) => {
              if (map.has(x)) {
                Object.assign(map.get(x), rest);
              } else {
                map.set(x, { x, ...rest });
              }

              return map;
            }, new Map())
            .values(),
        )}
        margin={{
          top: 8,
          right: 8,
          bottom: xLabel ? 24 : 0,
          left: yLabel ? 0 : -24,
        }}
      >
        <CartesianGrid stroke={dataColors.grid} vertical={false} />

        <XAxis
          dataKey="x"
          axisLine={false}
          tickFormatter={
            xAxisType === "date" ? (value: string) => formatDate(i18n, "monthYearShort", value) : undefined
          }
          label={xLabel && <Label value={xLabel} position="bottom" fontSize={14} fill={dataColors.label} />}
          fontSize={12}
          tickLine={false}
          padding={{ left: 20, right: 20 }}
        />
        <YAxis
          width={yLabel ? ANALYTICS_LABELED_Y_AXIS_WIDTH : undefined}
          domain={[0, maxY || "auto"]}
          tickFormatter={formatYTick || ((val) => String(Math.round(val * 10) / 10))}
          axisLine={false}
          tickLine={false}
          tickCount={6}
          label={
            yLabel ? (
              <Label
                value={yLabel}
                angle={-90}
                position="left"
                textAnchor="middle"
                offset={-15}
                fontSize={14}
                fill={dataColors.label}
              />
            ) : undefined
          }
          fontSize={12}
          padding={{ top: 20, bottom: 10 }}
        />

        <Tooltip content={<CustomTooltip data={data} hoverLabel={hoverLabel} />} />

        {data.map((series) => (
          <Line
            key={series.label}
            type="linear"
            dataKey={series.label}
            stroke={series.color}
            strokeWidth={2}
            dot={<CustomDot fill={series.color} />}
            activeDot={{ r: 9, fill: series.color, fillOpacity: 0.13, strokeWidth: 0 }}
            connectNulls={false}
            name={series.label}
          />
        ))}
      </RechartsLineChart>
    </ResponsiveContainer>
  );
}

function CustomTooltip<TLabel extends string = "">({
  active,
  payload,
  label,
  hoverLabel,
  data,
}: Partial<{
  active: boolean;
  payload: { payload: { x: string } & { [key in TLabel]: number } }[];
  label: TLabel;
}> & {
  hoverLabel: Props["hoverLabel"];
  data: Props["data"];
}) {
  if (!active || !payload) {
    return null;
  }

  const relatedPoints = data.map((series) => ({
    label: series.label || "",
    dataPoint: series.dataPoints.find((p) => p.x === label) || { x: label || "", y: 0 },
    color: series.color,
  }));

  const originalDataPoint = {
    x: relatedPoints[0].dataPoint.x,
    y: relatedPoints[0].dataPoint.y,
  };

  return (
    <ChartTooltip>
      {hoverLabel({
        ...originalDataPoint,
        relatedPoints,
      })}
    </ChartTooltip>
  );
}
